import { useState, useRef, useEffect } from 'react'
import { LogOut, ChevronDown, PlugZap, MonitorPlay, UserCog } from 'lucide-react'
import { logout, getUserInfo, isEnforcementOfficer, accountConsoleUrl } from '@/services/keycloakService'
import { useNavigate } from 'react-router-dom'
import { OutlookAddinModal } from './OutlookAddinModal'
import { useBranding } from '@/hooks/useBranding'
import { BrandIdentity } from './BrandIdentity'

/** At the foot of the profile menu: Parkeze's policies and a way to reach it. */
const LEGAL_LINKS = [
  { label: 'Privacy', href: 'https://parkeze.com/privacy-policy' },
  { label: 'Terms', href: 'https://parkeze.com/terms-of-service' },
  { label: 'Support', href: 'mailto:support@parkeze.com' },
]

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')
}

export function Header() {
  const navigate = useNavigate()
  const { brand } = useBranding()
  const user = getUserInfo()
  const displayName = (user?.name || user?.preferred_username || 'Guest') as string
  const email = (user?.email || '') as string
  const initials = getInitials(displayName)

  const [menuOpen, setMenuOpen] = useState(false)
  const [outlookModalOpen, setOutlookModalOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    if (menuOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      setMenuOpen(false)
      // Back to the button that opened it, as a keyboard user expects.
      menuRef.current?.querySelector('button')?.focus()
    }
    if (menuOpen) document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [menuOpen])

  return (
    <>
    <header
      className="relative z-40 overflow-visible bg-card border-b border-border px-4 py-2.5 flex items-center justify-between"
    >
      <button
        type="button"
        onClick={() => navigate('/home')}
        className="flex items-center min-w-0 mr-3 hover:opacity-80 transition-opacity"
      >
        <BrandIdentity brand={brand} />
      </button>

      <div className="flex items-center gap-3">
        {isEnforcementOfficer() && (
          <button
            type="button"
            onClick={() => {
              // Notifications need a click to be allowed; this is the click.
              // The desk module takes sound from the same activation itself.
              // Full screen is the kiosk's own toggle.
              if ('Notification' in window && Notification.permission === 'default') {
                void Notification.requestPermission()
              }
              navigate('/kiosk')
            }}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted transition-colors"
            title="Open the officer kiosk"
            aria-label="Kiosk"
          >
            <MonitorPlay className="w-4 h-4" />
            <span className="hidden sm:inline">Kiosk</span>
          </button>
        )}

        {/* Profile */}
        <div
          className="relative"
          ref={menuRef}
          // Tabbing out of the menu closes it, as clicking outside does.
          onBlur={(e) => { if (!menuRef.current?.contains(e.relatedTarget as Node)) setMenuOpen(false) }}
        >
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-muted transition-colors"
            aria-expanded={menuOpen}
            aria-controls="profile-menu"
            aria-label={`Account menu, ${displayName}`}
          >
            <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold select-none">
              {initials}
            </div>
            <span className="text-sm font-medium hidden md:inline max-w-[120px] truncate">
              {displayName}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden md:block" />
          </button>

          {menuOpen && (
            <div id="profile-menu" className="absolute right-0 top-full mt-1.5 w-56 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
              <div className="px-3 py-2.5 border-b border-border">
                <p className="text-sm font-medium truncate">{displayName}</p>
                {email && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{email}</p>
                )}
              </div>
              <a
                href={accountConsoleUrl()}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <UserCog className="w-4 h-4" />
                Manage account
              </a>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setOutlookModalOpen(true) }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <PlugZap className="w-4 h-4" />
                Add to Outlook
              </button>
              <button
                type="button"
                onClick={logout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
              <div className="flex items-center gap-3 px-3 pt-2 pb-1.5 mt-1 border-t border-border text-xs">
                {LEGAL_LINKS.map(({ label, href }) => (
                  <a
                    key={label}
                    href={href}
                    {...(href.startsWith('https:') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    onClick={() => setMenuOpen(false)}
                    className="text-muted-foreground hover:text-foreground hover:underline transition-colors"
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
    {outlookModalOpen && <OutlookAddinModal onClose={() => setOutlookModalOpen(false)} />}
  </>
  )
}
